import { defineStore } from 'pinia'

import { bus } from '~/services/bus.ts'
import { hitTransformations } from '~/services/hitTransformations.ts'
import { type CaseHit, type CaseModelDetailed, type CaseSource, type CaseSourceName } from '~/types.ts'
import { withReset } from './index.ts'

export function getHitsByDataId(hits: Record<number, CaseHit>) {
  const hitsByDataId: Record<string | number, CaseHit> = {}

  for (const hit of Object.values(hits)) {
    hitsByDataId[hit.data.id] = hit
  }

  return hitsByDataId
}

export interface Source extends CaseSource {
  status: 'loading' | 'loaded' | 'error'
}

export function createHitsStore(sourceName: CaseSourceName, prefix = '') {
  return defineStore(`${prefix}${capitalizeFirst(sourceName)}`, () => {
    const name = sourceName
    const loading = ref<number[]>([])
    const selected = ref<(number | string)[]>([])
    const sources = reactive<Record<number, Source>>({})
    const originalHits = shallowRef<Record<number, CaseHit>>({})

    const $reset = withReset({
      loading,
      selected,
      sources,
      originalHits,
    })

    const hitsByDataId = computed(() => getHitsByDataId(originalHits.value))
    const hits = computed((): CaseHit[] => Object.values(originalHits.value).map((hit) => hitTransformations(hit)))
    const positive = computed((): CaseHit[] => hits.value.filter((hit) => hit.resolution === 'positive'))
    const negative = computed((): CaseHit[] => hits.value.filter((hit) => hit.resolution === 'negative'))
    const unresolved = computed((): CaseHit[] => hits.value.filter((hit) => hit.resolution === 'unresolved'))

    /**
     * The ids of all hits that are marked as similar by another article.
     */
    const similarHitIds = computed((): Set<string> => {
      if (name !== 'news') return new Set<string>()
      const similarIds = new Set<string>()

      for (const hit of hits.value) {
        for (const id of hit.similar_data_ids ?? []) {
          similarIds.add(id)
        }
      }

      return similarIds
    })

    function searching(source: CaseSource) {
      if (loading.value.includes(source.id)) {
        return
      }

      if (!(source.id in sources)) {
        addSource(source)
      }

      setLoading(source.id)
      removeUnresolvedHitsByDataSourceId(source.data_source_id)
    }

    function updateSource(source: CaseSource & { hits: CaseHit[] }) {
      addSource(source)
      setLoaded(source.id)
      updateHits(source.hits)
    }

    function loadHitsFromCase(caseModel: CaseModelDetailed) {
      const sourceHits = caseModel.hits?.filter((hit) => hit.source === name) ?? []
      setHits(sourceHits)

      caseModel.sources.filter((source) => source.data_source.source === name).forEach((source) => addSource(source))
    }

    async function resolve({ ids, resolution, comment = null, risk = null }: { ids: number[]; resolution: string; comment?: string | null; risk?: string | null }) {
      const hitsToResolve = Object.values(hits.value).filter((hit) => ids.includes(hit.id))

      if (hitsToResolve.length === 0) {
        return
      }

      const caseSourcesToResolve = [...new Set(hitsToResolve.map((hit) => hit.data_source_id))]
        .map((dataSourceId) => Object.values(sources).find((source) => source.data_source.id === dataSourceId))
        .filter((source) => !!source)
      caseSourcesToResolve.forEach((source) => setLoading(source.id))

      ids = hitsToResolve.map((hit) => hit.id)
      try {
        const { data } = await api.patch<CaseModelDetailed>(
          'case-hits',
          {
            resolution,
            comment,
            risk,
            source: name,
            case_id: hitsToResolve[0].case_id,
            ids,
          },
          { key: ids },
        )

        updateHits(data.hits)
        bus.emit('hits.resolved')
      } catch (error) {
        console.error(error)
        $message.error($t('api-error'))
      } finally {
        caseSourcesToResolve.forEach((source) => setLoaded(source.id))
      }
    }

    function loadHitsFromSearch({ source, hits }: { source: CaseSource; hits: CaseHit[] }) {
      // Adding organization sources service adds hits from new sources.
      // So the source should be added in case its new.
      addSource(source)
      removeUnresolvedHitsByDataSourceId(source.data_source_id)
      setLoaded(source.id)
      updateHits(hits)
    }

    function addSource(source: CaseSource) {
      sources[source.id] = { ...copyObject(source), status: 'loaded' }
    }

    function deactivateSource(id: number) {
      if (!(id in sources)) return

      const source = sources[id]
      removeUnresolvedHitsByDataSourceId(source.data_source_id)
      source.active = false
      source.searched_at = null
    }

    function setLoading(id: number) {
      sources[id].status = 'loading'

      if (!loading.value.includes(id)) {
        loading.value.push(id)
      }
    }

    function setLoaded(id: number) {
      const index = loading.value.indexOf(id)
      if (index >= 0) {
        loading.value.splice(index, 1)
      }

      if (id in sources) {
        sources[id].status = 'loaded'
      }
    }

    function setError(id: number) {
      const index = loading.value.indexOf(id)
      if (index >= 0) {
        loading.value.splice(index, 1)
      }

      if (id in sources) {
        sources[id].status = 'error'
      }
    }

    function setHits(hits: CaseHit[]) {
      originalHits.value = keyBy('id', hits)
    }

    function updateHits(updatedHits: CaseHit[]) {
      const hits = Object.assign({}, originalHits.value)
      for (const hit of updatedHits) {
        hits[hit.id] = hit
      }
      originalHits.value = hits
    }

    function removeUnresolvedHitsByDataSourceId(dataSourceId: number) {
      const hits = Object.assign({}, originalHits.value)

      for (const id in hits) {
        if (hits[id].data_source_id === dataSourceId && hits[id].resolution === 'unresolved') {
          delete hits[id]
        }
      }

      originalHits.value = hits
    }

    return {
      name,
      loading,
      selected,
      sources,
      originalHits,
      hitsByDataId,
      hits,
      positive,
      negative,
      unresolved,
      similarHitIds,
      $reset,
      searching,
      updateSource,
      loadHitsFromCase,
      resolve,
      loadHitsFromSearch,
      addSource,
      deactivateSource,
      setLoading,
      setLoaded,
      setError,
      setHits,
      updateHits,
      removeUnresolvedHitsByDataSourceId,
    }
  })
}
